/* ============================================================
   Product Quiz — "Find Your Match"
   Section-scoped state machine. Triangulation = majority vote:
   each option votes for an outcome slot; the most-voted slot wins
   (ties broken by result order). The winning result panel is
   revealed with its line items. Adding the set routes through
   window.CartAPI; if any product has multiple variants, the global
   UISheet prompts the shopper to choose options for each first.
   ============================================================ */
(function () {
  /* ---------- shared helpers ---------- */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c];
    });
  }
  function selEsc(v) { return String(v).replace(/["\\]/g, '\\$&'); }

  function cartAdd(items) {
    if (!items || !items.length) return Promise.resolve();
    if (window.CartAPI && typeof window.CartAPI.addItems === 'function') {
      return window.CartAPI.addItems(items);
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) { return r.json(); })
      .then(function () { document.dispatchEvent(new CustomEvent('cart:item-added', { detail: {} })); });
  }

  // Build [{name, values[], idx}] from a product's variant option arrays.
  function deriveOptions(prod) {
    var names = prod.optionNames || [];
    return names.map(function (name, idx) {
      var seen = {}, values = [];
      (prod.variants || []).forEach(function (v) {
        var val = v.options[idx];
        if (val != null && !seen[val]) { seen[val] = 1; values.push(val); }
      });
      return { name: name, values: values, idx: idx };
    });
  }
  // Is `value` for option `idx` reachable given the other current choices?
  function valueAvailable(prod, idx, value, chosen) {
    return prod.variants.some(function (v) {
      if (!v.available) return false;
      if (String(v.options[idx]) !== String(value)) return false;
      for (var k = 0; k < chosen.length; k++) {
        if (k === idx) continue;
        if (chosen[k] != null && String(v.options[k]) !== String(chosen[k])) return false;
      }
      return true;
    });
  }
  function resolveVariant(prod, chosen) {
    if (chosen.some(function (c) { return c == null; })) return null;
    var match = null;
    prod.variants.some(function (v) {
      var ok = chosen.every(function (c, k) { return String(v.options[k]) === String(c); });
      if (ok) { match = v; return true; }
      return false;
    });
    return match;
  }
  function isColorOpt(name) { return /colou?r/i.test(name || ''); }
  function swatchHex(name) {
    try {
      if (window.UntamedColorSwatches && typeof window.UntamedColorSwatches.resolve === 'function') {
        var hex = window.UntamedColorSwatches.resolve(name);
        if (hex) return hex;
      }
    } catch (e) {}
    var n = String(name || '').trim().toLowerCase().replace(/\s+/g, '');
    return /^[a-z]+$/.test(n) ? n : '';
  }

  function init(section) {
    if (section.__pqInit) return;
    section.__pqInit = true;

    var cfg = { questionCount: 0, resultOrder: [] };
    var cfgEl = section.querySelector('[data-pq-config]');
    if (cfgEl) { try { cfg = JSON.parse(cfgEl.textContent); } catch (e) {} }

    var steps = Array.prototype.slice.call(section.querySelectorAll('[data-pq-step]'));
    var results = Array.prototype.slice.call(section.querySelectorAll('[data-pq-result]'));
    var segs = Array.prototype.slice.call(section.querySelectorAll('.pq__seg'));
    var mediaSteps = Array.prototype.slice.call(section.querySelectorAll('[data-pq-media-step]'));
    var mediaResults = Array.prototype.slice.call(section.querySelectorAll('[data-pq-media-result]'));
    var qCount = cfg.questionCount || steps.length;

    var step = 0;
    var answers = [];
    var resultShown = false;

    function show(el, on) { if (el) el.hidden = !on; }

    // Segments are nav: a segment is clickable only if its step is BEHIND the
    // current position (go back, never forward). On the result screen every
    // step is behind, so all become clickable.
    function renderProgress() {
      segs.forEach(function (seg, i) {
        var done = resultShown ? true : (i < step);
        var active = resultShown ? false : (i === step);
        var clickable = resultShown ? true : (i < step);
        seg.classList.toggle('done', done);
        seg.classList.toggle('active', active);
        seg.classList.toggle('is-clickable', clickable);
        if ('disabled' in seg) seg.disabled = !clickable;
      });
    }

    function showStep(i) {
      resultShown = false;
      steps.forEach(function (el) { show(el, parseInt(el.getAttribute('data-pq-step'), 10) === i); });
      mediaSteps.forEach(function (el) { show(el, parseInt(el.getAttribute('data-pq-media-step'), 10) === i); });
      results.forEach(function (el) { show(el, false); });
      mediaResults.forEach(function (el) { show(el, false); });
      step = i;
      renderProgress();
    }

    function computeWinner() {
      var counts = {};
      answers.forEach(function (slot) { if (slot) counts[slot] = (counts[slot] || 0) + 1; });
      var order = (cfg.resultOrder && cfg.resultOrder.length)
        ? cfg.resultOrder
        : results.map(function (el) { return el.getAttribute('data-pq-result'); });
      var best = null, bestCount = -1;
      order.forEach(function (slot) {
        var c = counts[slot] || 0;
        if (c > bestCount) { bestCount = c; best = slot; }
      });
      if (best === null || !section.querySelector('[data-pq-result="' + selEsc(best) + '"]')) {
        best = order[0] || (results[0] && results[0].getAttribute('data-pq-result'));
      }
      return best;
    }

    function showResult() {
      var slot = computeWinner();
      resultShown = true;
      steps.forEach(function (el) { show(el, false); });
      mediaSteps.forEach(function (el) { show(el, false); });
      results.forEach(function (el) { show(el, el.getAttribute('data-pq-result') === slot); });
      mediaResults.forEach(function (el) { show(el, el.getAttribute('data-pq-media-result') === slot); });
      renderProgress();
    }

    function pick(outcome) {
      answers[step] = outcome;
      if (step + 1 >= qCount) showResult(); else showStep(step + 1);
    }
    function reset() { answers = []; showStep(0); }

    /* ---------- add to bag (with variant sheet) ---------- */
    function readLineItems(resultEl) {
      return Array.prototype.slice.call(resultEl.querySelectorAll('[data-pq-li]')).map(function (li) {
        var d = { variants: [] };
        var s = li.querySelector('[data-pq-li-data]');
        if (s) { try { d = JSON.parse(s.textContent); } catch (e) {} }
        d.options = deriveOptions(d);
        return d;
      });
    }

    function addSet(addBtn) {
      var resultEl = addBtn.closest('[data-pq-result]');
      if (!resultEl) return;
      var lis = readLineItems(resultEl);
      var resolved = [];
      var need = [];
      lis.forEach(function (d) {
        if ((d.variants || []).length <= 1) {
          if (d.defaultVariantId) resolved.push({ id: d.defaultVariantId, quantity: 1 });
        } else {
          need.push(d);
        }
      });
      if (need.length === 0) {
        var items = resolved;
        if (!items.length) {
          try { items = JSON.parse(addBtn.getAttribute('data-pq-variants')); } catch (e) { items = []; }
        }
        addBtn.disabled = true;
        cartAdd(items).then(function () { addBtn.disabled = false; }).catch(function () { addBtn.disabled = false; });
        return;
      }
      openVariantSheet(need, resolved);
    }

    function openVariantSheet(need, resolved) {
      var sheetId = section.getAttribute('data-pq-sheet');
      var sheetEl = sheetId ? document.querySelector('[data-ui-sheet="' + selEsc(sheetId) + '"]') : null;
      var body = sheetEl ? sheetEl.querySelector('[data-pq-sheet-body]') : null;
      if (!body || !window.UISheet) { cartAdd(resolved); return; } // graceful fallback

      var states = need.map(function (p) {
        var chosen = p.options.map(function (o) { return o.values.length === 1 ? o.values[0] : null; });
        return { p: p, chosen: chosen };
      });

      function allResolved() { return states.every(function (st) { return resolveVariant(st.p, st.chosen); }); }

      function blockFor(st, pi) {
        var p = st.p;
        return {
          pi: pi, title: p.title, image: p.image,
          options: p.options.map(function (o) {
            var color = isColorOpt(o.name);
            return {
              name: o.name, oi: o.idx, isColor: color,
              selected: color ? st.chosen[o.idx] : null,
              values: o.values.map(function (val) {
                return { value: val, selected: String(st.chosen[o.idx]) === String(val), available: valueAvailable(p, o.idx, val, st.chosen), hex: color ? swatchHex(val) : null };
              })
            };
          })
        };
      }

      var VP = (window.UntamedSheets && window.UntamedSheets.vp) || {};
      function render() {
        var ready = allResolved();
        body.innerHTML = window.UntamedSheets.renderVariantPicker(states.map(blockFor), {
          intro: VP.intro || 'Pick options for the pieces below, then add the set to your bag.',
          addAttr: 'data-pq-sheet-add',
          addLabel: VP.add || 'Add set to bag',
          selectLabel: VP.select || 'Select options',
          ready: ready
        });
        bind();
      }

      function bind() {
        body.querySelectorAll('[data-vp-val]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            if (btn.getAttribute('aria-disabled') === 'true') return;
            var pi = parseInt(btn.getAttribute('data-vp-pi'), 10);
            var oi = parseInt(btn.getAttribute('data-vp-oi'), 10);
            var val = btn.getAttribute('data-vp-val');
            var st = states[pi];
            st.chosen[oi] = String(st.chosen[oi]) === String(val) ? null : val;
            render();
          });
        });
        var add = body.querySelector('[data-pq-sheet-add]');
        if (add) add.addEventListener('click', function () {
          if (!allResolved()) return;
          var items = resolved.slice();
          states.forEach(function (st) {
            var v = resolveVariant(st.p, st.chosen);
            if (v) items.push({ id: v.id, quantity: 1 });
          });
          add.disabled = true;
          cartAdd(items).then(function () { window.UISheet.close(); })
            .catch(function () { add.disabled = false; });
        });
      }

      render();
      window.UISheet.open(sheetId, { title: 'Choose your options' });
    }

    /* ---------- events ---------- */
    section.addEventListener('click', function (e) {
      var opt = e.target.closest('[data-pq-option]');
      if (opt && section.contains(opt)) { e.preventDefault(); pick(opt.getAttribute('data-pq-outcome')); return; }
      var seg = e.target.closest('[data-pq-seg]');
      if (seg) { if (!seg.disabled) showStep(parseInt(seg.getAttribute('data-pq-seg'), 10)); return; }
      if (e.target.closest('[data-pq-retake]')) { e.preventDefault(); reset(); return; }
      var add = e.target.closest('[data-pq-add]');
      if (add) { e.preventDefault(); addSet(add); return; }
    });

    showStep(0);
  }

  function boot() { document.querySelectorAll('[data-pq]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
